import type { HTMLAttributes, ReactNode } from 'react'
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { DropzoneOptions } from 'react-dropzone'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import type { Media } from 'src/services/apollo/generated/graphql'
import { fetchGetMedia } from 'src/services/apollo/media'
import { cx } from 'src/utils/cx'
import { getPublicUrl } from 'src/utils/media'
import { uploadMedia } from 'src/utils/upload'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import {
  DEFAULT_ACCEPTED_MIME_TYPES,
  useMediaUpload,
} from 'src/utils/useMediaUpload'
import { isAppWebview } from 'src/utils/userAgent'
import { TaskMediaPicker } from '../GenerationTask/TaskMediaPicker'
import type { UIButtonProps } from '../ui/Button'
import { UIButton } from '../ui/Button'

// =====================
// Configuration Interfaces
// =====================

/**
 * Feature configuration
 */
export interface FeatureConfig {
  dragDrop?: boolean
  uploadButton?: boolean
  taskPicker?: boolean
  customPicker?: boolean
  nativeGallery?: boolean
  nativeCamera?: boolean
  buttons?: boolean
}

/**
 * Text labels configuration
 */
export interface LabelsConfig {
  fromTask?: string
  fromUpload?: string
  fromNativeGallery?: string
  fromNativeCamera?: string
  uploading?: string
  uploadFailed?: string
  placeholder?: string | ((isNative: boolean) => string)
}

/**
 * Main component props with improved organization
 */
export interface ImageSourceProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'onChange'
> {
  // =====================
  // Core Props
  // =====================
  value?: Media
  onChange?: (value?: Media) => void
  disabled?: boolean

  // =====================
  // Upload Configuration
  // =====================
  supportedMimeTypes?: string[]

  // =====================
  // Styling Configuration
  // =====================
  size?: 'sm' | 'md' | 'lg' | 'custom'

  // =====================
  // Event Handlers
  // =====================
  onUpload?: (uploading: boolean) => void
  onImageSelect?: (file: File) => void
  preHandleImage?: (file: File) => Promise<File> | File
  onTaskMediaPicked?: (taskMedia: Media) => void

  // =====================
  // Configuration Objects
  // =====================

  /**
   * Feature toggles
   */
  features?: FeatureConfig

  /**
   * Text labels (falls back to i18n if not provided)
   */
  labels?: LabelsConfig

  // =====================
  // Component Customization
  // =====================
  buttonProps?:
    | Omit<UIButtonProps, 'onPress' | 'key' | 'children'>
    | {
        default?: Omit<UIButtonProps, 'onPress' | 'key' | 'children'>
        taskPicker?: Omit<UIButtonProps, 'onPress' | 'key' | 'children'>
        upload?: Omit<UIButtonProps, 'onPress' | 'key' | 'children'>
        gallery?: Omit<UIButtonProps, 'onPress' | 'key' | 'children'>
        camera?: Omit<UIButtonProps, 'onPress' | 'key' | 'children'>
        customPicker?: Omit<UIButtonProps, 'onPress' | 'key' | 'children'>
      }
  buttonGroupProps?: Omit<HTMLAttributes<HTMLDivElement>, 'children'>
  dropzoneOptions?: DropzoneOptions
  taskPickerShowFileUpload?: boolean

  // =====================
  // Element Overrides
  // =====================
  placeholderElement?: ReactNode | ((isWebView: boolean) => ReactNode)
  fileHandleElement?: (state: {
    action: (file?: File) => Promise<void>
  }) => ReactNode

  // =====================
  // Custom Picker
  // =====================
  customPickerElement?: (
    open: boolean,
    onPick: (media: Media) => void,
    onCancel: () => void,
  ) => ReactNode
  customPickerLabel?: string
}

/**
 * Exposed methods for the ImageSource component
 */
export interface ImageSourceRef {
  click: () => void
  abort: () => void
}

// =====================
// Default Configurations
// =====================

const DEFAULT_FEATURES: Required<FeatureConfig> = {
  dragDrop: true,
  uploadButton: true,
  taskPicker: true,
  customPicker: false,
  nativeGallery: true,
  nativeCamera: true,
  buttons: true,
}

// =====================
// Helper Functions
// =====================

/**
 * Merges user configuration with defaults
 */
function mergeConfig<T>(defaultConfig: T, userConfig?: Partial<T>): T {
  return { ...defaultConfig, ...userConfig }
}

/**
 * Gets the image URL from media object or file
 */
function getImageUrl(media?: Media, file?: File): string | undefined {
  if (media) {
    return getPublicUrl(media)
  }
  if (file) return URL.createObjectURL(file)
  return undefined
}

/**
 * ImageSource component allows users to select or upload images from various sources
 *
 * ## About the Action Buttons
 * - **Task Media Picker**: Opens a dialog to select images from existing tasks.
 * - **Upload**: Simulate click on the file input to upload images.
 *   - Desktop Web: Open the OS's file picker.
 *   - Mobile Web: Open a menu to ask the user where to get the image from (e.g., Gallery, Camera, Files).
 *   - Native App Webview: This button will not be rendered.
 * - **Native Gallery**: Opens the native photo gallery using JS Bridge.
 * - **Native Camera**: Opens the native camera using JS Bridge.
 *
 * ## Action Button Visibility
 * - **Desktop Web**: Task Media Picker; Upload button;
 * - **Mobile Web**: Task Media Picker; Upload button;
 * - **Native App Webview**: Task Media Picker; Native Gallery; Native Camera;
 */
export const ImageSource = forwardRef<ImageSourceRef, ImageSourceProps>(
  (
    {
      // =====================
      // Core Props
      // =====================
      value: media,
      onChange: setMedia = () => {
        // noop
      },
      disabled,

      // =====================
      // Upload Configuration
      // =====================
      supportedMimeTypes,

      // =====================
      // Styling Configuration
      // =====================
      size = 'md',

      // =====================
      // Event Handlers
      // =====================
      onUpload = () => {
        // noop
      },
      onImageSelect = () => {
        // noop
      },
      preHandleImage = (i: File) => i,
      onTaskMediaPicked = () => {
        // noop
      },

      // =====================
      // Configuration Objects
      // =====================
      features,
      labels,

      // =====================
      // Component Customization
      // =====================
      buttonProps,
      buttonGroupProps,
      dropzoneOptions,
      taskPickerShowFileUpload = false,

      // =====================
      // Element Overrides
      // =====================
      placeholderElement,
      fileHandleElement,

      // =====================
      // Custom Picker
      // =====================
      customPickerElement,
      customPickerLabel,

      // =====================
      // HTML Attributes
      // =====================
      className,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation(['action', 'generation', 'common'])
    const [file, setFile] = useState<File>()
    const [taskPickerOpen, setTaskPickerOpen] = useState(false)
    const [customPickerOpen, setCustomPickerOpen] = useState(false)

    // =====================
    // Configuration Merging
    // =====================

    const config = useMemo(() => {
      const mergedFeatures = mergeConfig(DEFAULT_FEATURES, features)

      return {
        features: mergedFeatures,
      }
    }, [features])

    // =====================
    // Derived Values
    // =====================

    const mimeTypes = supportedMimeTypes || DEFAULT_ACCEPTED_MIME_TYPES

    const mimeTypesAccept = useMemo(
      () =>
        mimeTypes.reduce(
          (acc: Record<string, string[]>, type: string) => {
            acc[type] = []
            return acc
          },
          {} as Record<string, string[]>,
        ),
      [mimeTypes],
    )

    const imageUrl = useMemo(() => {
      return getImageUrl(media, file)
    }, [media, file])

    // Get size class
    const sizeClass = useMemo(() => {
      switch (size) {
        case 'sm':
          return 'size-[200px] md:size-[250px]'
        case 'md':
          return 'size-[300px] md:size-[400px]'
        case 'lg':
          return 'size-[400px] md:size-[500px]'
        case 'custom':
          return '' // No size class applied, user controls via className
        default:
          return 'size-[300px] md:size-[400px]' // Default to md
      }
    }, [size])

    // =====================
    // Upload Logic (kept intact as requested)
    // =====================

    const setMediaRef = useRef(setMedia)
    setMediaRef.current = setMedia

    const uploadAbortRef = useRef<AbortController | null>(null)

    const [uploadError, setUploadError] = useState<Error>()
    const [uploadProgress, setUploadProgress] = useState(0)
    const [uploading, upload] = useLoadingCallback(
      async (file: File, signal?: AbortSignal) => {
        onUpload(true)
        try {
          setUploadError(undefined)
          setUploadProgress(0)
          const res = await uploadMedia(file, {
            onProgress: p => setUploadProgress(p),
            signal,
          })
          setMediaRef.current(res.media)
          setFile(undefined)
        } catch (e) {
          if (
            (e as DOMException)?.name === 'AbortError' ||
            (e as DOMException)?.name === 'CanceledError'
          )
            return
          console.error(e)
          setUploadError(e as Error)
          toast.error(
            (e as Error)?.message || t('common:error:failed-to-upload'),
          )
        } finally {
          onUpload(false)
          uploadAbortRef.current = null
        }
      },
      [onUpload],
    )

    const onDrop = useCallback(async (files: File[]) => {
      const file = files[0]
      if (file) {
        onImageSelect(file)
        const handledFile = await preHandleImage(file)
        setFile(handledFile)
        upload(handledFile)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const { getRootProps, getInputProps, inputRef } = useDropzone({
      onDrop,
      multiple: false,
      disabled: disabled || !config.features.dragDrop,
      accept: mimeTypesAccept,
      ...dropzoneOptions,
    })

    const currentFileAction = useCallback(
      async (fileFromArg?: File) => {
        if (!fileFromArg) return
        setFile(fileFromArg)
        upload(fileFromArg)
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [media],
    )

    const handleMediaId = useCallback(
      async (mediaId: string) => {
        const response = await fetchGetMedia({ id: mediaId })
        if (response.data?.media) {
          setMedia(response.data.media)
        }
        return response.data?.media
      },
      [setMedia],
    )

    const handleTaskPickerSelect = useCallback(
      async (mediaId: string) => {
        const media = await handleMediaId(mediaId)
        if (media) onTaskMediaPicked?.(media)
      },
      [handleMediaId, onTaskMediaPicked],
    )

    const handleFileSelect = useCallback(
      (files: File[]) => {
        const file = files[0]
        if (!file) return
        const controller = new AbortController()
        uploadAbortRef.current = controller
        onImageSelect(file)
        Promise.resolve(preHandleImage(file)).then(handledFile => {
          setFile(handledFile)
          upload(handledFile, controller.signal)
        })
      },
      [onImageSelect, preHandleImage, upload],
    )

    const isWebView = isAppWebview()

    const nativeUpload = useMediaUpload({
      acceptedMimeTypes: mimeTypes,
      onMediaId: handleMediaId,
    })

    const handleUploadClick = () => {
      inputRef.current?.click()
    }

    // Expose click method to parent component
    useImperativeHandle(
      ref,
      () => ({
        click: () => setTaskPickerOpen(true),
        abort: () => uploadAbortRef.current?.abort(),
      }),
      [],
    )

    const placeholderElementContent = useMemo(() => {
      if (typeof placeholderElement === 'function') {
        return placeholderElement(isWebView)
      }
      return (
        placeholderElement ?? (
          <div
            className="text-center font-bold underline"
            dangerouslySetInnerHTML={{
              __html:
                t('action:image-source.hint', {
                  br: '<br />',
                  context: isWebView ? 'native' : '',
                }) ?? '',
            }}
          />
        )
      )
    }, [placeholderElement, isWebView, t])

    const content = (
      <div
        className={cx(
          sizeClass,
          `
        border-2 border-dashed rounded-2xl
        flex justify-center items-center
        transition-all hover:transition-all
        text-primary-light font-mono
        `,
          {
            'hover:bg-hover/80 text-black/80 dark:hover:text-white/80  cursor-pointer':
              !disabled && config.features.dragDrop,
          },
          className,
        )}
        {...props}
        {...(config.features.dragDrop ? getRootProps() : {})}
      >
        {imageUrl ? (
          <div className="w-full h-full relative flex items-center overflow-hidden">
            <div
              className="w-full h-full bg-center bg-contain bg-no-repeat rounded-2xl"
              style={{
                backgroundImage: `url(${imageUrl})`,
              }}
            />
            {uploadError ? (
              <div className="absolute inset-0 p-8 bg-black bg-opacity-70 text-white flex flex-col justify-center items-center">
                <span>
                  {labels?.uploadFailed || t('common:error:failed-to-upload')}
                </span>
                <span className="whitespace-pre-wrap break-all">
                  {uploadError?.message}
                </span>
              </div>
            ) : uploading ? (
              <div className="absolute inset-0 bg-black bg-opacity-70 text-white flex flex-col items-center justify-center gap-3 rounded-2xl">
                <span>{labels?.uploading || t('action:uploading')}</span>
                <div className="w-32 h-1.5 bg-white/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-purple-500 rounded-full transition-[width] duration-300"
                    style={{ width: `${uploadProgress * 100}%` }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          placeholderElementContent
        )}

        {!nativeUpload.hasMultipleUploadSources && (
          <input {...getInputProps()} />
        )}
        {!uploading && !uploadError
          ? fileHandleElement?.({ action: currentFileAction })
          : null}
      </div>
    )

    // Render buttons below the image area
    const renderButtons = () => {
      if (!config.features.buttons) {
        return null
      }

      // Handle both old and new buttonProps formats
      const getButtonProps = (
        buttonType:
          | 'taskPicker'
          | 'upload'
          | 'gallery'
          | 'camera'
          | 'customPicker',
      ) => {
        if (!buttonProps) return { 'data-button-type': buttonType }

        // If buttonProps has specific button configs (new format)
        if (
          'default' in buttonProps ||
          'taskPicker' in buttonProps ||
          'upload' in buttonProps ||
          'gallery' in buttonProps ||
          'camera' in buttonProps ||
          'customPicker' in buttonProps
        ) {
          const specificProps = buttonProps[buttonType] ?? {}
          const defaultProps = buttonProps.default ?? {}
          const { variant: specificVariant, ...specificRest } = specificProps
          const { variant: defaultVariant, ...defaultRest } = defaultProps

          return {
            'data-button-type': buttonType,
            variant: specificVariant ?? defaultVariant ?? 'outlined',
            ...defaultRest,
            ...specificRest,
          }
        } else {
          // Old format - apply to all buttons
          const { variant, ...rest } = buttonProps as Omit<
            UIButtonProps,
            'onPress' | 'key' | 'children'
          >
          return {
            'data-button-type': buttonType,
            variant: variant ?? 'outlined',
            ...rest,
          }
        }
      }

      const buttons = []

      if (config.features.taskPicker) {
        buttons.push(
          <UIButton
            key="task-picker"
            onPress={() => setTaskPickerOpen(true)}
            busy={uploading}
            {...getButtonProps('taskPicker')}
          >
            {labels?.fromTask || t('action:image-source.from-task')}
          </UIButton>,
        )
      }

      // For desktop/mobile web/ios webview - show upload button
      if (
        config.features.uploadButton &&
        !nativeUpload.hasMultipleUploadSources
      ) {
        buttons.push(
          <UIButton
            key="upload"
            onPress={handleUploadClick}
            {...getButtonProps('upload')}
          >
            {labels?.fromUpload || t('action:image-source.from-upload')}
          </UIButton>,
        )
      }

      // For native app webview - show separate gallery and camera buttons
      // only android
      if (
        nativeUpload.hasMultipleUploadSources &&
        config.features.nativeGallery
      ) {
        buttons.push(
          <UIButton
            key="gallery"
            onPress={nativeUpload.openNativeGallery}
            {...getButtonProps('gallery')}
          >
            {labels?.fromNativeGallery ||
              t('action:image-source.from-native-photo-gallery')}
          </UIButton>,
        )
      }

      if (
        nativeUpload.hasMultipleUploadSources &&
        config.features.nativeCamera
      ) {
        buttons.push(
          <UIButton
            key="camera"
            onPress={nativeUpload.openNativeCamera}
            {...getButtonProps('camera')}
          >
            {labels?.fromNativeCamera ||
              t('action:image-source.from-native-camera')}
          </UIButton>,
        )
      }

      if (config.features.customPicker && customPickerElement) {
        buttons.push(
          <UIButton
            key="custom-picker"
            onPress={() => setCustomPickerOpen(true)}
            {...getButtonProps('customPicker')}
          >
            {customPickerLabel}
          </UIButton>,
        )
      }

      if (buttons.length === 0) {
        return null
      }

      const { className, ...buttonGroupRest } = buttonGroupProps ?? {}

      return (
        <div
          className={className ?? 'flex flex-col gap-2 flex-wrap w-full'}
          {...buttonGroupRest}
        >
          {buttons}
        </div>
      )
    }

    return (
      <>
        {content}

        {renderButtons()}

        {/* Task Media Picker Dialog */}
        {config.features.taskPicker && (
          <TaskMediaPicker
            open={taskPickerOpen}
            onClose={() => setTaskPickerOpen(false)}
            onSubmit={handleTaskPickerSelect}
            showFileUpload={taskPickerShowFileUpload}
            onSelectedFiles={handleFileSelect}
          />
        )}

        {/* Custom Picker */}
        {config.features.customPicker &&
          customPickerElement &&
          customPickerElement(
            customPickerOpen,
            media => {
              setMedia(media)
              setCustomPickerOpen(false)
            },
            () => {
              setCustomPickerOpen(false)
            },
          )}
      </>
    )
  },
)
